import React from "react";
import { Link } from "react-router-dom";
import ServiceFactory from "../../service";

/*
 * Shared bits for the Forecasts redesign (2026-10-09; mockup
 * sales-forecasts-inventory-redesign-2026-10-09.html boards 4 + 5).
 *
 * Data rails:
 *   forecast API  /v1/forecast/product-forecast/overview      every product forecast + interest curve (one call)
 *                 /v1/forecast/product-forecast/{id}/summary  one forecast's header facts
 *                 /v1/forecast/product-forecast/{id}|location|place|contact|recommendation  detail sections
 *   sales API     /v1/sales/explorer/products/year            last 12 months + next 12 months per product
 *                 /v1/sales/forecast/year?product=            the product page's "What's coming" model
 * ServiceFactory parses with JSONbig, so forecast/contact ids above 2^53 survive; always String() them.
 */

export function fetchForecast(api) {
  const Service = ServiceFactory.getType(api, "forecast");
  return new Service().getAsync();
}

// `product` base is the bare /v1 root: /sales/*, /inventory/*.
export function fetchV1(api) {
  const Service = ServiceFactory.getType(api, "product");
  return new Service().getAsync();
}
export const num = v => (Number(v) || 0).toLocaleString("en-US", {
  maximumFractionDigits: 0
});
export function usdShort(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(a >= 1e7 ? 1 : 2).replace(/\.?0+$/, "")}M`;
  if (a >= 1e4) return `$${Math.round(n / 1e3)}K`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
}
export function usd0(v) {
  return (Number(v) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}
export function shortDate(v) {
  if (!v) return "";
  const d = new Date(String(v).length <= 10 ? `${v}T00:00:00` : v);
  return isNaN(d) ? "" : d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}
export function monthLabel(v) {
  const s = String(v || "");
  const d = new Date(s.length === 7 ? `${s}-01T00:00:00` : s.slice(0, 10) + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit"
  });
}

// Outlook from last vs next 12 months of sales. ±10% is "Steady".
export function outlookOf(last, next) {
  const l = Number(last) || 0,
    n = Number(next) || 0;
  if (l <= 0 && n <= 0) return {
    key: "none",
    label: "No sales yet",
    tone: "none"
  };
  if (l <= 0) return {
    key: "up",
    label: "Growing",
    tone: "up"
  };
  const r = n / l;
  if (r >= 1.1) return {
    key: "up",
    label: "Growing",
    tone: "up"
  };
  if (r <= 0.9) return {
    key: "down",
    label: "Slowing",
    tone: "down"
  };
  return {
    key: "flat",
    label: "Steady",
    tone: "flat"
  };
}

// The AI "score" answer is one of five words; say it plainly.
export function performanceOf(answer) {
  const s = String(answer || "").toLowerCase();
  if (s.indexOf("excellent") > -1) return {
    label: "Top performer",
    tone: "up"
  };
  if (s.indexOf("above average") > -1) return {
    label: "Strong performer",
    tone: "up"
  };
  if (s.indexOf("below average") > -1) return {
    label: "Below average performer",
    tone: "down"
  };
  if (s.indexOf("average") > -1) return {
    label: "Average performer",
    tone: "flat"
  };
  if (s.indexOf("poor") > -1) return {
    label: "Weak performer",
    tone: "down"
  };
  return null;
}

// Plain topic labels for the AI recommendation categories.
export const REC_TOPICS = {
  marketing: "Marketing",
  location: "Where to sell it",
  score: "How it is doing",
  pricing: "Pricing",
  stock: "Stock"
};
export const recTopic = c => REC_TOPICS[String(c || "").toLowerCase()] || String(c || "Other").replace(/^\w/, m => m.toUpperCase());

// No em-dashes in copy (house rule): soften any that arrive in AI text.
export const plain = s => String(s || "").replace(/\s*—\s*/g, ", ").trim();

/** Forecasts header: title + subtitle + Your products / New designs toggle + Test a new design. */
export function ForecastsHeader({
  active,
  designs
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "fc-hdr"
  }, /*#__PURE__*/React.createElement("div", {
    className: "fc-hdr-main"
  }, /*#__PURE__*/React.createElement("h1", null, "Forecasts"), /*#__PURE__*/React.createElement("span", {
    className: "fc-sub"
  }, "What each product is likely to sell next, and how a new design would do before you make it")), /*#__PURE__*/React.createElement("span", {
    className: "fc-hdr-actions"
  }, /*#__PURE__*/React.createElement("span", {
    className: "fc-seg",
    role: "tablist",
    "aria-label": "Forecast type"
  }, /*#__PURE__*/React.createElement(Link, {
    to: "/Forecast/Product",
    className: `fc-seg-item${active === "products" ? " on" : ""}`,
    role: "tab",
    "aria-selected": active === "products"
  }, "Your products"), /*#__PURE__*/React.createElement(Link, {
    to: "/Forecast/Similarity",
    className: `fc-seg-item${active === "designs" ? " on" : ""}`,
    role: "tab",
    "aria-selected": active === "designs"
  }, "New designs", designs != null ? /*#__PURE__*/React.createElement("span", {
    className: "fc-seg-n"
  }, num(designs)) : null)), /*#__PURE__*/React.createElement(Link, {
    to: "/Forecast/Similarity?new=1",
    className: "fc-btn primary"
  }, "Test a new design")));
}

/** Count of "test a design" runs, for the toggle badge. Fails soft to null. */
export async function fetchDesignCount() {
  try {
    const d = await fetchForecast("/forecast/product-similarity-forecast?$top=1");
    if (d && d.count != null) return Number(d.count);
    return Array.isArray(d && d.items) ? d.items.length : null;
  } catch (e) {
    return null;
  }
}
export function Wait({
  short
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `fc-wait${short ? " short" : ""}`
  });
}